import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getPolicyParitySummary } from "@/lib/policy-parity.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const Route = createFileRoute("/_authenticated/app/admin/policy-parity")({
  component: PolicyParityPage,
});

function pct(n: number) {
  return `${(n * 100).toFixed(2)}%`;
}

function PolicyParityPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["policy-parity-summary"],
    queryFn: () => getPolicyParitySummary(),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading parity data…</div>;
  }
  if (error || !data) {
    return <div className="p-6 text-sm text-destructive">Failed to load parity data.</div>;
  }

  const { current_mode, overrides, last24h, recent_mismatches } = data;
  const empty = last24h.total === 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Policy parity</h1>
          <p className="text-sm text-muted-foreground">
            Diagnostic view of legacy vs. unified policy evaluation. Read-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Read mode</span>
          <Badge
            variant={current_mode === "legacy" ? "secondary" : current_mode === "shadow" ? "outline" : "default"}
          >
            {current_mode}
          </Badge>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>24h evaluations</CardDescription>
            <CardTitle className="text-3xl">{last24h.total.toLocaleString()}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>24h match rate</CardDescription>
            <CardTitle className="text-3xl">
              {empty ? "—" : pct(last24h.match_rate)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>24h mismatches</CardDescription>
            <CardTitle className="text-3xl">{last24h.mismatches.toLocaleString()}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>By domain</CardTitle>
          <CardDescription>
            Grouped by descriptor. Domains with no evaluations in the last 24h are omitted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {empty ? (
            <p className="text-sm text-muted-foreground">
              No parity data yet. This is expected while read mode is <code>legacy</code>.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Matches</TableHead>
                  <TableHead className="text-right">Mismatches</TableHead>
                  <TableHead className="text-right">Match rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {last24h.by_descriptor.map((row) => (
                  <TableRow key={row.descriptor_id}>
                    <TableCell className="font-mono text-xs">{row.descriptor_id}</TableCell>
                    <TableCell className="text-right">{row.total}</TableCell>
                    <TableCell className="text-right">{row.matches}</TableCell>
                    <TableCell className="text-right">
                      {row.mismatches > 0 ? (
                        <span className="text-destructive">{row.mismatches}</span>
                      ) : (
                        row.mismatches
                      )}
                    </TableCell>
                    <TableCell className="text-right">{pct(row.match_rate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent mismatches</CardTitle>
          <CardDescription>Up to 20 most recent divergences.</CardDescription>
        </CardHeader>
        <CardContent>
          {recent_mismatches.length === 0 ? (
            <p className="text-sm text-muted-foreground">No mismatches recorded.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Domain</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Diff</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent_mismatches.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">
                      {new Date(r.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.descriptor_id}</TableCell>
                    <TableCell className="font-mono text-xs">{r.subject_key}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{r.mode}</Badge>
                    </TableCell>
                    <TableCell>
                      <details>
                        <summary className="cursor-pointer text-xs text-muted-foreground">
                          view
                        </summary>
                        <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted p-2 text-[10px]">
                          {JSON.stringify(r.diff, null, 2)}
                        </pre>
                      </details>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {overrides.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Per-tenant overrides</CardTitle>
            <CardDescription>Tenants running in a non-default read mode.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overrides.map((o) => (
                  <TableRow key={o.tenant_id}>
                    <TableCell className="font-mono text-xs">{o.tenant_id}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{o.mode}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {new Date(o.updated_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}